import React, { useMemo, useState } from 'react';
import { Collapse, DatePicker, Checkbox, Button, Typography, Tooltip, Flex, Modal, Table, Divider, theme, Tree, Alert } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import moment from 'moment';
import { alertTypeColumns, dataSource, defaulFilterObj, getRiskSpanClass, privacyOptions, riskToTypesMap } from '../helpers';
import { convertObjectOfArraysToSortedString } from '../../../utils';
import _ from 'lodash';
const { RangePicker } = DatePicker;
const { Text } = Typography;
const { useToken } = theme;

const FilterPanel = ({ isCollapsed, alertTypes, metrics, onApplyFilters, appliedFilters }) => {
  const [isInfoModalVisible, setIsInfoModalVisible] = useState(false);
  const {token} = useToken();
  const [selectedFilters, setSelectedFilters] = useState(appliedFilters);

  const createTreeData = () => {
    return Object.entries(riskToTypesMap).map(([riskLevel, alertTypeValues]) => {
      const children = alertTypes?.filter(alertType => 
        alertTypeValues.includes(alertType.value)
      ).map(alertType => ({
        title: alertType.label,
        key: alertType.value,
      })) || [];
      
      return {
        title: <span className={getRiskSpanClass(riskLevel)}>{riskLevel}</span>,
        key: riskLevel,
        children
      };
    }).filter(node => node.children.length > 0);
  };

  const handleTreeCheck = (checkedKeys) => {
    const checkedAlertTypes = checkedKeys?.filter(key => alertTypes.find((alertType) => alertType.value === key))
    handleCheckboxChange('type', checkedAlertTypes)
  };


  const items = [
    {
      key: '1',
      label: (
        <>
          <Flex align="center" gap={token.marginSM}>
            <Text>Timeframe</Text>
            <Tooltip title="Date selection is limited to past 30 days" placement="top">
              <InfoCircleOutlined style={{ color: token.colorTextDisabled }} />
            </Tooltip>
          </Flex>
        </>
      ),
      children: (
        <RangePicker
          disabledDate={(current) => {
            const today = moment();
            const past30 = moment().subtract(30, 'days');
            return current < past30.startOf('day') || current > today.endOf('day');
          }}
          onChange={(value) => handleCheckboxChange('dateRange', value)}
          style={{ width: '100%' }}
          value={selectedFilters?.dateRange || []}
          format={'MMM DD, YYYY'}
        />
      ),
    },
    {
      key: '2',   
      label: 'Privacy Risk Score & Alerts',
      children: (
        <>
          <Checkbox
            checked={(selectedFilters?.type?.length === alertTypes?.length) }
            indeterminate={
              (selectedFilters?.type?.length > 0 && selectedFilters?.type?.length < alertTypes?.length)
            }
            onChange={(e) => {
              const checked = e.target.checked;
              handleCheckboxChange('type', checked ? alertTypes?.map((opt) => opt.value) : []);
              handleCheckboxChange('risk', checked ? privacyOptions : []);
            }}
          >
            Select All
          </Checkbox>
           <Tree
            checkable
            defaultExpandAll
            treeData={createTreeData()}
            checkedKeys={selectedFilters?.type}
            onCheck={handleTreeCheck}
          />
          <Divider />
          <Button onClick={() => setIsInfoModalVisible(true)} type="link" size="small">
            About all alerts
          </Button>
        </>
      ),
    },
    {
      key: '4',
      label: 'Metrics',
      children: (
        <>
          <Checkbox
            checked={selectedFilters?.metric?.length === metrics?.length}
            indeterminate={
              selectedFilters?.metric?.length > 0 && selectedFilters?.metric?.length < metrics?.length
            }
            onChange={(e) => {
              const checked = e.target.checked;
              handleCheckboxChange('metric', checked ? metrics : []);
            }}
          >
            Select All
          </Checkbox>
          <Checkbox.Group
            style={{ display: 'flex', flexDirection: 'column' }}
            options={(metrics || []).map((opt) => ({
              label: opt === 'Tracking Tech' ? 'Tracking Tech Detected' : opt,
              value: opt,
            }))}
            onChange={(val) => handleCheckboxChange('metric', val)}
            value={selectedFilters?.metric}
          />
        </>
      ),
    },
  ];

  const handleFilterChange = (key, value) => {
      setSelectedFilters((prevFilters) => (
        {
          ...prevFilters,
          [key]: value
        }
    ))
  };

  const handleClearAll = () => {
    setSelectedFilters(defaulFilterObj)
    onApplyFilters(defaulFilterObj);
  };
  
  const handleCheckboxChange = (key, value) => {
    handleFilterChange(key, value ?? []);
  };

  const handleApplyFilter = () => {
    onApplyFilters(selectedFilters);
  }

  const areFiltersSame = convertObjectOfArraysToSortedString(selectedFilters) === convertObjectOfArraysToSortedString(appliedFilters);

  return (
    <>
      {isCollapsed && (
        <>
          <Collapse className="alerts-v2-collapse" items={items} defaultActiveKey={1} accordion />

          <div className="filter-footer">
            <div className="filter-panel-btn">
              <Button onClick={() => handleClearAll()} type="link">
                Reset
              </Button>
              <Button onClick={handleApplyFilter} disabled={areFiltersSame} type="primary">
                Apply Filter
              </Button>
            </div>
            <div className="filter-panel-msg">
              {!areFiltersSame && (
                <Alert
                  message={
                    <>
                      Changes Detected in Filter options. Click on <Text italic>Apply Filter</Text> to refresh data
                    </>
                  }
                  type="warning"
                  showIcon
                />
              )}
            </div>
          </div>
        </>
      )}
      <Modal
        open={isInfoModalVisible}
        title="All Alerts"
        onCancel={() => setIsInfoModalVisible(false)}
        footer={[
          <Button key="close" type="primary" onClick={() => setIsInfoModalVisible(false)}>
            Close
          </Button>,
        ]}
        width={1000}
      >
        <Table dataSource={dataSource} columns={alertTypeColumns} pagination={false} bordered size="small" />
      </Modal>
    </>
  );
};

export default FilterPanel;